"use client";

import { ArrowUpIcon } from "lucide-react";
import { useRef, useState } from "react";
import { track } from "../analytics";
import { Chevron, mkButton, mkLink } from "../components/MkButton";
import { HeroStage } from "../hero/HeroStage";
import type { HeroAsk } from "../hero/LiveStore";
import { DEMO_STORE } from "../site";

const SUGGESTIONS = [
	"Snacks for 6 watching the game",
	"Kabsa for 6",
	"عندكم حليب المراعي؟",
	"Cheapest rice",
];

/**
 * The first screen: what Hephon Commerce is in one line and the two ways forward (a demo, or
 * trying it now), then the product playing three moments a visitor can pick from. Under the
 * phone, the visitor can ask the demo store anything and the phone becomes that store.
 */
export function Hero() {
	const [ask, setAsk] = useState<HeroAsk | null>(null);
	const [text, setText] = useState("");
	const stage = useRef<HTMLDivElement>(null);
	const field = useRef<HTMLInputElement>(null);

	const still = () =>
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	const send = (question: string, source: "typed" | "suggestion") => {
		const trimmed = question.trim();
		if (!trimmed) {
			return;
		}
		track("demo_store_asked", { question: trimmed, source });
		setAsk((last) => ({ id: (last?.id ?? 0) + 1, text: trimmed }));
		setText("");
		stage.current?.scrollIntoView({
			behavior: still() ? "auto" : "smooth",
			block: "center",
		});
	};

	return (
		<section aria-labelledby="hero-title" className="overflow-hidden pb-10">
			<div className="mk-container pt-12 text-center sm:pt-16">
				<h1 id="hero-title" className="mk-kicker text-(--mk-ink-2)">
					The AI shopping assistant for supermarkets
				</h1>
				<p className="mk-h1 mx-auto mt-3 max-w-4xl">
					Every question becomes a basket.
				</p>
				<p className="mk-lead mx-auto mt-5 max-w-2xl text-(--mk-ink-2)">
					For retailers in Saudi Arabia and the UAE. It answers
					shoppers in Arabic and English, in your app, on WhatsApp and
					on the phone, from your live catalog.
				</p>
				<div className="mt-8 flex flex-wrap items-center justify-center gap-3">
					<a href="/demo" className={mkButton({ size: "lg" })}>
						Book a demo
					</a>
					<button
						type="button"
						onClick={() => {
							field.current?.scrollIntoView({
								behavior: still() ? "auto" : "smooth",
								block: "center",
							});
							field.current?.focus({ preventScroll: true });
						}}
						className={mkButton({
							variant: "secondary",
							size: "lg",
						})}
					>
						Try it yourself
					</button>
				</div>
			</div>

			<div
				ref={stage}
				className="mt-10 h-[650px] scroll-mt-20 sm:mt-12 sm:h-[750px] lg:h-[770px]"
			>
				<HeroStage ask={ask} />
			</div>

			<div className="mk-container mt-8 text-center">
				{ask ? (
					<div
						className="mk-pop mb-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-5"
						aria-live="polite"
					>
						<p className="text-(--mk-ink-2) text-[15px]">
							This is a demo store. Yours would answer from your
							own catalog.
						</p>
						<a href="/demo" className={mkButton({ size: "sm" })}>
							Book a demo
						</a>
					</div>
				) : (
					<p className="mk-h3 mb-5">
						Now ask it yourself.
						<span className="text-(--mk-muted)">
							{" "}
							It is a real store with over {DEMO_STORE.products}{" "}
							products.
						</span>
					</p>
				)}
				<form
					className="mx-auto max-w-xl"
					onSubmit={(event) => {
						event.preventDefault();
						send(text, "typed");
					}}
				>
					<label htmlFor="hero-ask" className="sr-only">
						Ask {DEMO_STORE.name}, a demo store, for anything
					</label>
					<div className="flex h-14 items-center gap-2 rounded-full bg-(--mk-mist) ps-6 pe-2 transition-shadow duration-(--duration-hover) focus-within:shadow-[0_0_0_2px_var(--mk-signal)]">
						<input
							ref={field}
							id="hero-ask"
							value={text}
							onChange={(event) => setText(event.target.value)}
							placeholder={
								ask
									? "Ask something else"
									: `Ask ${DEMO_STORE.name} for anything`
							}
							autoComplete="off"
							enterKeyHint="send"
							maxLength={300}
							className="min-w-0 flex-1 bg-transparent text-[17px] outline-none placeholder:text-(--mk-faint)"
						/>
						<button
							type="submit"
							aria-label="Ask"
							disabled={!text.trim()}
							className="grid size-10 shrink-0 place-items-center rounded-full bg-(--mk-ink) text-(--mk-white) outline-none transition-[opacity,transform] duration-(--duration-hover) active:scale-95 disabled:opacity-25 focus-visible:ring-2 focus-visible:ring-(--mk-signal) focus-visible:ring-offset-2"
						>
							<ArrowUpIcon className="size-[18px]" aria-hidden />
						</button>
					</div>
					<ul className="mt-4 flex flex-wrap justify-center gap-2">
						{SUGGESTIONS.map((suggestion) => (
							<li key={suggestion}>
								<button
									type="button"
									onClick={() =>
										send(suggestion, "suggestion")
									}
									dir="auto"
									className="rounded-full bg-(--mk-mist) px-3.5 py-1.5 text-(--mk-ink-2) text-[14px] outline-none transition-colors duration-(--duration-hover) hover:bg-(--mk-mist-2) hover:text-(--mk-ink) focus-visible:ring-2 focus-visible:ring-(--mk-signal)"
								>
									{suggestion}
								</button>
							</li>
						))}
					</ul>
				</form>
				<p className="mt-6">
					<a href="#channels-title" className={mkLink("text-[15px]")}>
						See where it works
						<Chevron />
					</a>
				</p>
			</div>
		</section>
	);
}
